import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import Login, { sanitizeRedirect } from "./pages/Login";
import LoginLink from "./pages/LoginLink";
import Dashboard from "./pages/Dashboard";
import Vacatures from "./pages/Vacatures";
import AuditTrail from "./pages/AuditTrail";
import Gebruikersbeheer from "./pages/Gebruikersbeheer";
import SwipeReview from "./pages/SwipeReview";
import PartnerConfig from "./pages/PartnerConfig";
import VacatureDetail from "./pages/VacatureDetail";
import Meldingen from "./pages/Meldingen";
import Taken from "./pages/Taken";
import Marktstatistieken, { MarktstatistiekenPlaceholder } from "./pages/Marktstatistieken";
import Werkgeversinzichten from "./pages/Werkgeversinzichten";
import AlertRegels from "./pages/AlertRegels";
import Systeemstatus from "./pages/Systeemstatus";
import SysteemstatusEmails from "./pages/SysteemstatusEmails";
import SheetsSync from "./pages/SheetsSync";
import Functietitels from "./pages/Functietitels";
import Beheer from "./pages/Beheer";
import MailGoedkeuring from "./pages/MailGoedkeuring";
import Toegangsverzoeken from "./pages/Toegangsverzoeken";
import Gesprekken from "./pages/Gesprekken";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import Toaster from "./components/Toaster";
import { ChatWidgetProvider } from "./components/ChatWidget";
import { WavesLogo } from "./components/WavesLogo";
import { SettingsDialogProvider, useSettingsDialog } from "./components/SettingsDialog";
import { HelpProvider } from "./components/help/HelpProvider";

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <WavesLogo className="h-7" />
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-waves-zee border-t-transparent" />
        <span className="text-[13px] text-waves-muted">Laden…</span>
      </div>
    </div>
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ("admin" | "kam" | "hmp" | "partner_admin")[];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) {
    const redirect = new URLSearchParams(location.search).get("redirect");
    return <Navigate to={sanitizeRedirect(redirect)} replace />;
  }
  return <Login />;
}

// Full-height page wrapper — pages using Layout use h-full, others manage their own height
function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      {children}
    </div>
  );
}

// Instellingen is a dialog, not a page: old links and bookmarks open it on
// top of the dashboard.
function OpenSettings() {
  const { openSettings } = useSettingsDialog();
  React.useEffect(() => { openSettings(); }, [openSettings]);
  return <Navigate to="/" replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  return (
    <ChatWidgetProvider enabled={isAuthenticated}>
      <HelpProvider>
      <SettingsDialogProvider enabled={isAuthenticated}>
      <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/login/link" element={<LoginLink />} />

      <Route path="/" element={
        <ProtectedRoute>
          <Page><Dashboard /></Page>
        </ProtectedRoute>
      } />

      <Route path="/vacatures" element={
        <ProtectedRoute allowedRoles={["admin", "kam"]}>
          <Page><Vacatures /></Page>
        </ProtectedRoute>
      } />

      <Route path="/vacatures/:partnerId" element={
        <ProtectedRoute>
          <Page><Vacatures /></Page>
        </ProtectedRoute>
      } />

      <Route path="/vacature/:vacatureId" element={
        <ProtectedRoute>
          <Page><VacatureDetail /></Page>
        </ProtectedRoute>
      } />

      <Route path="/swipe-review" element={
        <ProtectedRoute allowedRoles={["hmp"]}>
          <SwipeReview />
        </ProtectedRoute>
      } />

      <Route path="/audit-trail" element={
        <ProtectedRoute allowedRoles={["admin", "kam", "partner_admin"]}>
          <Page><AuditTrail /></Page>
        </ProtectedRoute>
      } />

      <Route path="/gebruikersbeheer" element={
        <ProtectedRoute allowedRoles={["admin", "kam", "partner_admin"]}>
          <Page><Gebruikersbeheer /></Page>
        </ProtectedRoute>
      } />

      <Route path="/beheer" element={
        <ProtectedRoute allowedRoles={["admin", "kam", "partner_admin"]}>
          <Page><Beheer /></Page>
        </ProtectedRoute>
      } />

      <Route path="/partnerbeheer" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page>
            <Layout title="Partnerbeheer" subtitle="Beheer partners en hun vestigingen">
              <PartnerConfig />
            </Layout>
          </Page>
        </ProtectedRoute>
      } />

      <Route path="/meldingen" element={
        <ProtectedRoute>
          <Page><Meldingen /></Page>
        </ProtectedRoute>
      } />

      <Route path="/taken" element={
        <ProtectedRoute>
          <Page><Taken /></Page>
        </ProtectedRoute>
      } />

      <Route path="/marktstatistieken" element={
        <ProtectedRoute>
          <Page><MarktstatistiekenPlaceholder/></Page>
          {/* <Page><Marktstatistieken /></Page> */}
        </ProtectedRoute>
      } />

      <Route path="/werkgeversinzichten" element={
        <ProtectedRoute>
          <Page><Werkgeversinzichten /></Page>
        </ProtectedRoute>
      } />


      <Route path="/alert-regels" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><AlertRegels /></Page>
        </ProtectedRoute>
      } />

      <Route path="/systeemstatus" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><Systeemstatus /></Page>
        </ProtectedRoute>
      } />

      <Route path="/systeemstatus/emails" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><SysteemstatusEmails /></Page>
        </ProtectedRoute>
      } />

      <Route path="/sheets-sync" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><SheetsSync /></Page>
        </ProtectedRoute>
      } />

      <Route path="/gesprekken" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><Gesprekken /></Page>
        </ProtectedRoute>
      } />

      <Route path="/functietitels" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Page><Functietitels /></Page>
        </ProtectedRoute>
      } />

      <Route path="/mail-goedkeuring" element={
        <ProtectedRoute allowedRoles={["admin", "kam"]}>
          <Page><MailGoedkeuring /></Page>
        </ProtectedRoute>
      } />

      <Route path="/toegangsverzoeken" element={
        <ProtectedRoute allowedRoles={["admin", "kam", "partner_admin"]}>
          <Page><Toegangsverzoeken /></Page>
        </ProtectedRoute>
      } />

      <Route path="/instellingen" element={
        <ProtectedRoute>
          <OpenSettings />
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </SettingsDialogProvider>
      </HelpProvider>
    </ChatWidgetProvider>
  );
}

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ErrorBoundary>
      <Toaster />
    </QueryClientProvider>
  );
}
